<template>
  <div v-if="canView" class="time-sheet-details-body">
    <TimeSheetBodyHeader
      :time-sheet="timeSheet"
      :can-save="canEdit"
      :can-reopen="canReopen"
      :can-expense="canExpense"
      :purchase-order-items="purchaseOrderLineItems"
      @export-as-pdf="exportAsPdf()"
      @save-time-sheet="saveTimeSheet()"
      @reopen-time-sheet="reopenTimeSheet()"
    />
    <TimeSheetDetailsTable
      :time-sheet="timeSheet"
      :can-edit="canEdit"
      @change="onTimeSheetChange"
    />
    <!-- IBM & Kyndryl check to display end client -->
    <div v-if="canSubmit" class="time-sheet-actions contractor-actions">
      <div
        v-if="
          this.isIBMOrKyndryl(timeSheet.assignment.client_reference_number) &&
            timeSheet.assignment.end_client_name
        "
        class="confirm-wrapper"
      >
        <!--show for IBM & Kyndryl only -->
        <div class="label">{{ $t('timeSheets.details.endClientConfirm') }}</div>
        <div class="end-client-confirm">
          <TolRadioButton
            button-label="Confirm"
            label="Confirm"
            name="confirmEndClient"
            :value="timeSheet.is_ibm_end_client_confirmed"
            @toggle="confirmIbmEndClient($event)"
          />
          <span>{{ timeSheet.assignment.end_client_name }} </span>
        </div>
        <div class="end-client-confirm">
          <TolRadioButton
            button-label="Replace"
            label="Replace"
            name="confirmEndClient"
            :value="timeSheet.is_ibm_endclient_confirmed"
            @toggle="confirmIbmEndClient($event)"
          />
          <span class="confirm-input">
            <InputField
              type="text"
              class="bordered"
              :value="timeSheet.assignment.end_client_name"
              :disabled="
                timeSheet.is_ibm_endclient_confirmed === true ||
                  timeSheet.is_ibm_endclient_confirmed === null
              "
              @input="changeIbmEndClient"
          /></span>
        </div>
        <div v-if="errorMsg" class="error-div">
          <font-awesome-icon icon="exclamation-circle" /> {{ errorMsg }}
        </div>
      </div>
      <TolButton
        :label="$t('timeSheet.actions.submit')"
        button-type="major"
        class="submit-button"
        icon-name="paper-plane"
        @click="submitTimeSheet()"
      />
    </div>
    <div v-else-if="canApproveOrReject" class="time-sheet-actions approver-actions">
      <div class="approver-comment">
        <input-field
          v-model="approverComment"
          :class="{ 'approver-error': approverCommentError }"
          :placeholder="$t('timeSheet.placeholders.commentsPlaceholder')"
          class="bordered"
          field-icon="comments"
          @focus="approverCommentError = false"
        />
      </div>
      <div class="time-sheet-major-actions">
        <div v-if="canApprove && timeSheet.status !== 'APPROVED'">
          <TolButton
            :label="$t('timeSheet.actions.approve')"
            button-type="major"
            icon-name="check"
            class="approve-button"
            @click="approverAction('APPROVE')"
          />
        </div>
        <div v-if="canReject && timeSheet.status !== 'REJECTED'">
          <TolButton
            :label="$t('timeSheet.actions.reject')"
            button-type="major"
            class="reject-button"
            icon-name="times"
            @click="approverAction('REJECT')"
          />
        </div>
        <div v-if="canProcess">
          <TolButton
            :label="$t('timeSheet.actions.process')"
            button-type="major"
            class="process-button"
            icon-name="check"
            @click="approverAction('PROCESS')"
          />
        </div>
      </div>
    </div>

    <div class="time-sheet-minor-actions">
      <template v-if="canSave">
        <TolButton
          :label="$t('timeSheet.actions.save')"
          button-type="major"
          class="save-button"
          icon-name="pencil-alt"
          @click="saveTimeSheet"
        />
      </template>

      <template v-if="canReopen">
        <TolButton
          :label="$t('timeSheet.actions.reopen')"
          button-type="major"
          icon-name="lock-open"
          @click="reopenTimeSheet"
        />
      </template>

      <template v-if="canExpense && canThisUserCreate">
        <TolButton
          :label="
            timeSheet.hasExpense
              ? $t('timeSheet.actions.viewExpense')
              : $t('timeSheet.actions.createExpense')
          "
          button-type="major"
          icon-name="dollar-sign"
          @click="expenseAction"
        />
      </template>
    </div>

    <div v-if="canViewAttachment && timeSheet && timeSheet.referenceNumber">
      <Attachment
        :reference-number="timeSheet.referenceNumber"
        owner="TIME_SHEET"
        @attachment-success="refreshAttachments"
      />
      <AttachmentsList
        attachment-type="TIME_SHEET"
        :attachments="attachments"
        :attachment-options="attachmentOptions"
        @delete-attachment="confirmAttachmentDelete"
        @refresh="refreshAttachments"
      />
    </div>
    <div class="time-sheet-history">
      <tol-button
        button-type="major"
        :label="showHideRecords"
        :icon-name="iconFor"
        @click="toggleHistoryRecordList()"
      />
    </div>
    <div v-if="showHideHistoryRecords" class="time-sheet-history">
      <div class="sub-header">{{ $t('timeSheets.history.title') }}</div>
      <hr />
      <TimeSheetHistoryList />
    </div>
    <update-modal v-if="isLoading">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import TimeSheetBodyHeader from './TimeSheetBodyHeader';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import ConfirmModal from '@/app/components/ConfirmModal';
import UpdateModal from '@/app/components/UpdateModal';
import TolButton from '@/app/components/TolButton';
import TolRadioButton from '@/app/components/TolRadioButton';
import InputField from '@/app/components/InputField';
import { TimeSheetDetailsActions, TimeSheetDetailsGetters } from '../time-sheet-details.keys';
import { timeSheetDetailsService } from '../time-sheet-details.service';
import TimeSheetDetailsTable from './TimeSheetDetailsTable';
import TimeSheetHistoryList from './TimeSheetHistoryList';
import * as R from 'ramda';
import Attachment from '@/app/components/attachments/Attachment';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { AppActions } from '@/app/app.keys';
import { userUtils } from '@/mixins/user-utils';

export default {
  name: 'TimeSheetDetailsBody',
  components: {
    Attachment,
    AttachmentsList,
    ConfirmModal,
    TimeSheetBodyHeader,
    TimeSheetDetailsTable,
    TolButton,
    TimeSheetHistoryList,
    TolRadioButton,
    InputField,
    UpdateModal
  },
  mixins: [userUtils],
  props: {
    timeSheet: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      entries: [],
      notes: [],
      approverComment: '',
      approverCommentError: false,
      replacedIbmEndClient: null,
      errorMsg: null,
      attachmentOptions: ['Invoice', 'Other'],
      showHideHistoryRecords: false,
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null
    };
  },
  computed: {
    showHideRecords() {
      return this.showHideHistoryRecords
        ? this.$t('timeSheets.details.hideHistory')
        : this.$t('timeSheets.details.showHistory');
    },
    iconFor() {
      return this.showHideHistoryRecords ? 'eye' : 'eye-slash';
    },
    isLoading() {
      return this.$store.getters[TimeSheetDetailsGetters.IsLoading];
    },
    isContractor() {
      return (
        this.currentUser &&
        this.currentUser.roles.map(r => r.role_name).some(role => role === 'Contractor')
      );
    },
    isApprover() {
      return (
        this.currentUser &&
        this.currentUser.roles.map(r => r.role_name).some(role => role === 'Client Contact')
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    timeSheetStatus() {
      return R.path(['status'], this.timeSheet);
    },
    canView() {
      return !!this.currentUser && !!this.currentUser.permissions['TIME_SHEET']['VIEW'];
    },
    canViewAttachment() {
      let isSameUser = this.timeSheet.assignment.contractor
        ? this.timeSheet.assignment.contractor.user_id === this.currentUser.id
        : false;
      return this.currentUser.permissions['TIME_SHEET']['VIEW_ATTACHMENT'] || isSameUser;
    },
    canEdit() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['TIME_SHEET']['SAVE'] &&
        R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['SAVE'])
      );
    },
    canSave() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['TIME_SHEET']['SAVE'] &&
        R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['SAVE'])
      );
    },
    canReopen() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['TIME_SHEET']['REOPEN'] &&
        R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['REOPEN'])
      );
    },
    canSubmit() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['TIME_SHEET']['SUBMIT'] &&
        R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['SUBMIT'])
      );
    },
    canExpense() {
      return (
        this.timeSheetStatus != 'OBSOLETE' &&
        this.timeSheet.assignment.expense_submittal === 'ONLINE'
      );
    },
    canThisUserCreate() {
      return !!this.currentUser && this.currentUser.permissions['EXPENSE_REPORT']['CREATE'];
    },
    canApproveOrReject() {
      return (
        !!this.currentUser &&
        ((!!this.currentUser.permissions['TIME_SHEET']['APPROVE'] &&
          R.contains(
            this.timeSheetStatus,
            this.currentUser.permissions['TIME_SHEET']['APPROVE']
          )) ||
          (!!this.currentUser.permissions['TIME_SHEET']['REJECT'] &&
            R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['REJECT'])))
      );
    },
    canApprove() {
      if (
        this.timeSheet.assignment.stakeholderClientContacts.some(
          item => item.user_id === this.currentUser.id
        )
      ) {
        const [approver] = this.timeSheet.assignment.stakeholderClientContacts.filter(
          item => item.user_id === this.currentUser.id
        );
        return approver.can_approve_time_sheets;
      } else {
        return (
          !!this.currentUser &&
          !!this.currentUser.permissions['TIME_SHEET']['APPROVE'] &&
          R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['APPROVE'])
        );
      }
    },
    canReject() {
      if (
        this.timeSheet.assignment.stakeholderClientContacts.some(
          item => item.user_id === this.currentUser.id
        )
      ) {
        const [approver] = this.timeSheet.assignment.stakeholderClientContacts.filter(
          item => item.user_id === this.currentUser.id
        );
        return approver.can_approve_time_sheets;
      } else {
        return (
          !!this.currentUser &&
          !!this.currentUser.permissions['TIME_SHEET']['REJECT'] &&
          R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['REJECT'])
        );
      }
    },
    canProcess() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['TIME_SHEET']['PROCESS'] &&
        R.contains(this.timeSheetStatus, this.currentUser.permissions['TIME_SHEET']['PROCESS'])
      );
    },
    attachments() {
      return this.$store.state.timeSheetsState.timeSheetDetailsState.attachments;
    },
    purchaseOrderLineItems() {
      const poDetails = this.timeSheet.assignment.purchaseOrderDetails;
      if (!poDetails) return [];
      const poWithItems = poDetails.find(po => po.lineItems && po.lineItems.length > 0);
      return poWithItems ? poWithItems.lineItems : [];
    }
  },
  watch: {
    timeSheet: {
      immediate: true,
      handler() {
        this.entries = this.timeSheet.timeSheetEntries;
        this.notes = this.timeSheet.timeSheetNotes;
        this.approverComment = this.timeSheet.approverComment;
      }
    }
  },
  mounted() {
    if (this.isContractor) {
      this.refreshAttachments();
    }

    if (this.canEdit || this.canSubmit) {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('timeSheets.details.editTitle'));
    } else if (this.canApproveOrReject) {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('timeSheets.details.reviewTitle'));
    }
  },
  methods: {
    toggleHistoryRecordList() {
      this.showHideHistoryRecords = !this.showHideHistoryRecords;
      if (this.showHideHistoryRecords) {
        this.$store.dispatch(
          TimeSheetDetailsActions.RetrieveTimeSheetHistory,
          this.timeSheet.referenceNumber
        );
      }
    },
    changeIbmEndClient(val) {
      this.replacedIbmEndClient = val.trim();
      this.errorMsg = null;
      this.$store.dispatch(TimeSheetDetailsActions.ChangeIbmEndClient, this.replacedIbmEndClient);
    },
    confirmIbmEndClient(val) {
      if (val === 'Confirm') {
        this.errorMsg = null;
      }
      const yesNo = val === 'Confirm';
      this.$store.dispatch(TimeSheetDetailsActions.ConfirmIbmEndClient, yesNo);
    },
    onTimeSheetChange({ entries, notes }) {
      this.entries = entries;
      this.notes = notes;
    },
    timeSheetAction(actionRequest) {
      this.$store.dispatch(TimeSheetDetailsActions.SaveTimeSheet, actionRequest);
    },
    saveTimeSheet() {
      this.timeSheetAction({
        action: 'SAVE',
        entries: this.entries,
        notes: this.notes,
        comment: this.approverComment
      });
    },
    reopenTimeSheet() {
      this.timeSheetAction({
        action: 'REOPEN',
        approverComment: this.approverComment,
        entries: this.entries,
        notes: this.notes
      });
    },
    submitTimeSheet() {
      if (
        this.isIBMOrKyndryl(this.timeSheet.assignment.client_reference_number) &&
        this.timeSheet.assignment.end_client_name &&
        (!this.timeSheet.is_ibm_endclient_confirmed && !this.replacedIbmEndClient)
      ) {
        this.errorMsg = this.$t('timeSheets.details.errors.confirmIbmEndClient');
        return;
      }
      this.timeSheetAction({
        action: 'SUBMIT',
        entries: this.entries,
        notes: this.notes
      });
    },
    expenseAction() {
      this.$store.dispatch(TimeSheetDetailsActions.ExpenseAction);
    },
    approverAction(action) {
      if (action === 'REJECT' && this.isNilOrEmpty(this.approverComment)) {
        this.approverCommentError = true;
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: this.$t('timeSheets.details.errors.commentMandatory')
        });
      } else {
        this.approverCommentError = false;
        this.$store.dispatch(TimeSheetDetailsActions.TimeSheetApproverAction, {
          action,
          approverComment: this.approverComment
        });
      }
    },
    isNilOrEmpty(value) {
      return R.isNil(value) || R.isEmpty(value);
    },
    exportAsPdf() {
      timeSheetDetailsService.downloadTimeSheetPdf(this.timeSheet.referenceNumber);
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    refreshAttachments() {
      this.$store.dispatch(
        TimeSheetDetailsActions.RetrieveTimeSheetAttachments,
        this.$route.params.id
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.time-sheet-details-body {
  background-color: $background-level-1;

  .time-sheet-actions {
    display: flex;
    background-color: $background-level-2;

    padding: 12px 60px;
    .spacer {
      flex-grow: 1;
    }
    .confirm-wrapper {
      display: flex;
      //margin-left: 60px;

      //border: 1px solid $blue;
      .end-client-confirm {
        display: flex;
        padding: 8px 0;
        align-items: center;
        margin-right: 15px;
        .radio-container {
          margin-bottom: 0;
        }

        span,
        input {
          display: inline-block;
          margin-left: 5px;
          font-weight: bold;
        }
        span.confirm-input {
          font-size: 1em;
          height: inherit;
          &:focus {
            outline: none;
          }
          &.error {
            border-bottom: 1px solid $red;
          }
        }

        input {
          border: 0;
        }
      }

      .error-div {
        display: flex;
        align-items: center;
        color: $red;
        background-color: $primary-error;

        padding: 8px;
        &:hover {
          cursor: pointer;
        }
        svg {
          margin-right: 15px;
        }
      }
    }
    .label {
      padding: 0 5px;
      margin-right: 12px;
      display: flex;
      align-items: center;
      font-weight: bold;
    }
  }
  .submit-button {
    margin-left: auto;
  }
  .time-sheet-major-actions,
  .time-sheet-minor-actions {
    display: flex;
  }
  .time-sheet-major-actions {
    margin-left: auto;
  }

  .time-sheet-minor-actions {
    width: 100%;
    background: $white;
    padding: $details-padding;
    text-align: right;
    border-top: 1px solid $grey;
  }

  .button-separator {
    margin: 0 ($details-padding/2);
    display: inline-block;
  }

  .approve-button,
  .process-button,
  .reject-button {
    margin-left: 10px;
  }

  .save-button {
    background-color: $green;
  }

  .reject-button {
    background-color: $red;
  }
  .approver-actions {
    background-color: $background-level-2;
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .contractor-approver-comment.approver-actions {
    justify-content: flex-start;
  }
  .approver-comment {
    width: 450px;
    &::placeholder {
      color: $text-color-light;
    }
  }
  .approver-error {
    border: 2px solid red;
  }
  .attachment-upload {
    margin-top: 10px;
  }

  .time-sheet-history {
    background-color: $background-level-2;
    margin: 10px auto;

    .sub-header {
      padding: $details-padding;
    }
  }

  .time-sheet-pdf-footer {
    display: none;
    font-size: 10px;
  }
  .bordered {
    border: 1px solid $blue;
  }
}

@media print {
  .time-sheet-details-body {
    background-color: $white;
    .time-sheet-actions,
    .submit-button,
    .approve-button,
    .time-sheet-major-actions,
    .time-sheet-minor-actions,
    .approver-actions,
    .approver-comment {
      display: none;
    }

    .time-sheet-history {
      margin: 0;
      .sub-header {
        padding: 0;
        font-size: 14px;
      }

      hr {
        margin: 0;
      }
    }

    .time-sheet-pdf-footer {
      display: table-footer-group;
      position: fixed;
      bottom: 0;
    }
  }
}

@media only screen and (max-width: 1024px) {
  .time-sheet-details-body {
    .time-sheet-actions {
      flex-direction: column;

      padding: 10px;
      &.approver-actions {
        .approver-comment,
        .time-sheet-major-actions {
          margin-bottom: 10px;
          width: 100%;
        }

        .time-sheet-major-actions {
          justify-content: center;
        }
      }
      .confirm-wrapper {
        flex-direction: column;
        margin: 0;
      }
    }
    .time-sheet-history {
      display: none;
    }

    .time-sheet-minor-actions {
      padding: $responsive-details-padding;
    }
  }
}
</style>
